import type { SubscriptionStatus, SubscriptionTier } from "@convex/validators";

/**
 * Pure subscription domain rules — the counterpart to `./credits.ts`, and
 * like it, the one place both `convex/` and `src/` may import from (nothing
 * under `convex/` may import from `src/`, so a value both sides need lives
 * here). This is a business rule, not a stored shape, so it belongs beside
 * the other rules in `convex/model/` rather than in `convex/validators.ts`,
 * which is deliberately validators and their `Infer`ed types only.
 */

/**
 * Subscription statuses that count as "the user has access" AND "the user
 * can spend" — ONE list serving both notions, not two. `past_due` is
 * included deliberately: the user confirmed (2026-09-17) this is the
 * intended business rule, not an inherited accident. Do not split this into
 * a separate "billing-active" vs "access-active" list, and do not drop
 * `past_due` because it looks like a lapsed payment.
 *
 * `convex/lib/activeSubscription.ts` (`selectActiveSubscription`) and
 * `src/helpers/subscription.ts` (`hasActiveSubscription`) both import this.
 * They used to keep hand-written copies of it in sync; they no longer do.
 */
export const ACTIVE_SUBSCRIPTION_STATUSES: readonly SubscriptionStatus[] = [
  "active",
  "past_due",
  "paused",
  "trialing",
];

/**
 * The user's plan: a subscription tier, or a non-subscription state.
 * - "payg": pay-as-you-go customer (bought credits, no subscription)
 * - "free": never bought credits or subscribed
 */
export type PlanTier = SubscriptionTier | "payg" | "free";

/** The active subscription's tier if any, else "payg" or "free". */
export function resolvePlanTier(
  subscriptionTier: SubscriptionTier | null | undefined,
  isCustomer: boolean,
): PlanTier {
  return subscriptionTier ?? (isCustomer ? "payg" : "free");
}
